<script>
const delays = [
  {
    value: 60 * 24,
    key: 'dias',
  },
  {
    value: 60,
    key: 'horas',
  },
  {
    value: 1,
    key: 'minutos',
  },
];

export default {
  inheritAttrs: false,

  props: {
    actions: {
      type: Object,
      required: true,
    },

    value: {
      type: Object,
      required: true,
    },

    isEmpty: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      action: null,
      option: null,
      options: null,
      loading: false,
      actionable: null,
      group: -1,
      delay: {
        value: 0,
        interval: 1,
      },
      validators: {
        'lead.send': this.validateLeadSend,
      },
    };
  },

  watch: {
    value: {
      deep: true,
      immediate: true,
      handler ({
        type,
        delay,
        option,
        actionable,
      }) {
        this.action = type || this.action;
        this.option = option || this.option;
        this.actionable = actionable || this.actionable;

        const parsed = Math.floor(Number(delay) / 60);

        if (! Number.isInteger(parsed)) {
          return;
        }

        const {
          value: interval = 1,
        } = delays.find((item) => parsed >= item.value) || {};

        this.delay.interval = interval;
        this.delay.value = parsed / interval;
      },
    },

    action: {
      immediate: true,
      handler (action) {
        if (! action) {
          this.options = null;
          this.actionable = null;

          return;
        }

        this.fetchOptions(action);
      },
    },
  },

  computed: {
    mapped () {
      return Object.keys(this.actions)
        .map((key) => {
          const action = this.actions[key];

          return {
            key,
            ...action,
          };
        });
    },

    translatedOptions () {
      return this.options.map((item) => ({ ...item, name: this.__(item.name) }));
    },

    actionableSelect () {
      const {
        action,
        translatedOptions,
        hasGroupOptions,
      } = this;

      return {
        'show-labels': false,
        'group-values': hasGroupOptions
          ? 'groupOptions'
          : '',
        'options': translatedOptions,
        'multiple': false,
        'group-label': 'name',
        'group-select': false,
        'placeholder': action.type ? action.type.placeholder : this.__('Selecione'),
        'track-by': 'name',
        'label': 'name',
        'deselect-label': 'Deselecionar item',
        'deselect-group-label': 'Deselecionar todos',
        'select-group-label': 'Selecionar todos',
        'select-label': 'Selecionar item',
        'selected-label': 'Selecionado',
      };
    },

    selected () {
      return this.mapped.find(({ key }) => this.action == key);
    },

    hasOptions () {
      return this.options instanceof Array && this.options.length > 0;
    },

    hasGroupOptions () {
      return this.hasOptions && this.options.find(({ groupOptions }) => groupOptions && groupOptions.length > 0) !== undefined;
    },

    showOptions () {
      if (this.hasOptions && this.selected.placeholder) {
        return true;
      }

      return this.options && this.options.placeholder;
    },

    delays: () => delays,

    listeners () {
      const {
        save,
        ...listeners
      } = this.$listeners;

      return listeners;
    },

    validator() {
      return this.validators[this.action] ?? null;
    },

    validValue () {
      const validator = this.validator;

      if (!validator) {
        return true;
      }

      return validator();
    },
  },

  methods: {
    async fetchOptions (type) {
      this.loading = true;

      try {
        const {
          data,
        } = await this.$http.get('/automation/action/value', {
          params: {
            type,
          },
        });

        this.options = data;
      } catch (e) {
        window.alert('Nenhuma opção encontrada.');
      }

      this.loading = false;
    },

    validateForSubmit () {
      const validator = this.validator;

      if (!validator) {
        return true;
      }

      return validator(true);
    },

    save () {
      const {
        delay,
        option,
        actionable,
        action: type,
        hasOptions,
      } = this;

      const validation = this.validateForSubmit();
      if (validation !== true) {
        return Alert.error(validation);
      }

      this.$emit('save', {
        type,
        option,
        delay: delay.value * delay.interval,
        value: hasOptions
          ? actionable.value
          : actionable,
      });
    },

    validateLeadSend(submit = false) {
      const url = this.actionable;
      const message = 'Insira uma URL válida!';

      if (!url) {
        return submit ? message : true;
      }

      const regex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;

      const valid = regex.test(url);

      return valid ? true : message;
    },
  },
};
</script>

<template>
  <AutomationBuilderModal
    @save="save"
    :items="mapped"
    :disabled="! options"
    v-bind="$attrs"
    v-model="action"
    v-on="listeners"
    :button="__('Escolher ação')"
    :title="__('Ações da campanha')"
    :placeholder="__('Encontre uma ação')"
  >
    <div
      v-if="selected && ! loading"
      class="row automation-builder-modal__row"
    >
      <div
        v-if="showOptions"
        class="col-md-12"
      >
        <div
          v-if="hasOptions"
          class="form-group"
        >
          <label>{{ __('Selecione') }}</label>

          <div
            class="automation-builder-modal__actionable"
          >
            <Multiselect
              v-bind="actionableSelect"
              v-model="actionable"
            />
          </div>
        </div>

        <div
          v-else
          class="form-group"
        >
          <AutomationBuilderActionEdit
            v-model="actionable"
            v-if="['integration.waboxapp.send', 'sms.send'].includes(selected.key)"
          />

          <textarea
            rows="5"
            class="form-control"
            v-model="actionable"
            v-else-if="selected.textarea"
            :maxlength="options.maxlength"
            :placeholder="__(options.placeholder)"
          />

          <input
            v-else
            class="form-control"
            v-model="actionable"
            :placeholder="__(options.placeholder)"
          />

          <p
            v-if="validValue !== true"
            class="invalid-value"
          >
            {{ validValue }}
          </p>
        </div>
      </div>

      <div
        class="col-md-12"
        v-if="selected.option_input"
      >
        <div class="form-group">
          <label
            v-text="__(selected.option_input.placeholder)"
          />

          <input
            v-model="option"
            class="form-control"
            :type="selected.option_input.type"
            :placeholder="__(selected.option_input.placeholder)"
          >
        </div>
      </div>

      <div class="col-md-12">
        <div class="form-group">
          <label
            class="automation-builder-modal__label"
          >
            {{ __('Executar após:') }}

            <i
              v-if="value && value.decision && value.decision.option === '0' && ! isEmpty"
            >
              1 dia +
            </i>
          </label>

          <div class="row">
            <div class="col-md-4">
              <input
                class="form-control"
                type="number"
                min="0"
                value="0"
                v-model="delay.value"
              >
            </div>

            <div class="col-md-8">
              <select
                class="form-control"
                v-model="delay.interval"
              >
                <option
                  :key="key"
                  v-text="__(key)"
                  :value="value"
                  v-for="({ key, value }) in delays"
                />
              </select>
            </div>

            <p class="automation-builder-modal__help col-md-12">
              {{ __('tempo em relação à ação anterior') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </AutomationBuilderModal>
</template>

<style lang="scss">
  .automation-builder-modal {
    &__actionable {
      ::v-deep .multiselect__option {
        padding: 6px 12px;
      }

      .multiselect__tags {
        span.multiselect__placeholder{
          width: 100%;
        }
      }

      input,
      select,
      .multiselect__single,
      .multiselect__placeholder {
        color: #3B334A;
        background-color: #F6F5F8;
      }
    }

    &__label {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .invalid-value {
    font-size: 11px;
    font-weight: 500;
    line-height: 18px;
    color: #dc3545;
    letter-spacing: -.17px;
    padding-left: 2px;
    padding-top: 2px;
  }
</style>
